import { SentryError } from '@ifixit/sentry';

// Fail a stalled request instead of leaving callers (e.g. the source/visual
// toggle) disabled forever waiting on a response that never comes.
const TIMEOUT_MS = 30_000;

/**
 * Minimal fetch-based client for the AjaxIO protocol, replacing the legacy
 * request class. Handlers registered with `$ajax->addHandler` on the
 * PHP page are invoked by POSTing form-encoded `AjaxIO_function` /
 * `AjaxIO_args` to the page's own URL; the response is either
 * `{ error: ... }` or `{ AjaxIOData: <result> }`.
 */
export function callAjaxIO<T>(funcName: string, ...args: unknown[]): Promise<T> {
   return callAjaxIOAt(window.location.href.split('#', 1)[0], funcName, ...args);
}

/**
 * Same protocol, but for handlers registered on a different page's URL
 * (e.g. the Answers index calls the view page's `watch` handler).
 */
export async function callAjaxIOAt<T>(
   url: string,
   funcName: string,
   ...args: unknown[]
): Promise<T> {
   const body = new URLSearchParams({
      AjaxIO_function: funcName,
      AjaxIO_args: JSON.stringify(args),
   });

   let response: Response;
   try {
      response = await fetch(url, {
         method: 'POST',
         body,
         signal: AbortSignal.timeout(TIMEOUT_MS),
      });
   } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      throw new SentryError(`AjaxIO ${funcName} request failed: ${reason}`);
   }

   if (!response.ok) {
      throw new SentryError(`AjaxIO ${funcName} failed with status ${response.status}`);
   }

   let payload: { error?: unknown; AjaxIOData?: T };
   try {
      payload = await response.json();
   } catch {
      // A 200 that isn't JSON (e.g. a PHP notice or HTML error page slipped
      // into the response) would otherwise throw a bare SyntaxError.
      throw new SentryError(`AjaxIO ${funcName} returned a non-JSON response`);
   }

   if (payload.error) {
      throw new SentryError(String(payload.error));
   }
   if (payload.AjaxIOData === undefined) {
      throw new SentryError(`AjaxIO ${funcName} returned no data`);
   }

   return payload.AjaxIOData as T;
}
